'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ApprovalsTabById from 'src/parts/tabs/ApprovalsTabById';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getGroupApprovals } from 'src/api/admin/approvals';

export const Route = createFileRoute('/_admin/groups/$groupId/approvals/')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || undefined,
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const approvals = await getGroupApprovals(params.groupId, { page: deps.page, sort: deps.sort });
		return { approvals };
	},
	component: ApprovalsPage,
});

function ApprovalsPage() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { approvals: initialApprovals } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const { data: approvals, refetch: refetchApprovals } = useQuery({
		queryKey: ['group-approvals', groupId, searchParams.page, searchParams.sort],
		queryFn: () => getGroupApprovals(groupId, { page: searchParams.page, sort: searchParams.sort }),
		initialData: initialApprovals,
	});

	const handleUpdate = () => {
		refetchApprovals();
		queryClient.invalidateQueries({ queryKey: ['group', groupId] });
		queryClient.invalidateQueries({ queryKey: ['group-approvals-overview', groupId] });
	};

	const handleReload = () => {
		refetchApprovals();
	};

	return (
		<ApprovalsTabById
			approvals={approvals}
			authenticatedUser={authenticatedUser!}
			onUpdate={handleUpdate}
			onReload={handleReload}
			onApprovalSelected={(id) =>
				navigate({ to: '/groups/$groupId/approvals/$approvalId', params: { groupId, approvalId: id } })
			}
		/>
	);
}
